import { fetchCampaignRequest, fetchCampaignMetadata, fetchCampaignRequestPossibleMatches } from '../../services/campaign';
import { useLoaderData } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import InformationTabs from '../../components/CampaignRequestDetails/InformationTabs';
import AssignedTo from '../../components/CampaignRequestDetails/AssignedTo';
import ActivitiesTabs from '../../components/CampaignRequestDetails/ActivitiesTabs';
import NameHeader from '../../components/CampaignRequestDetails/NameHeader';
import { useEffect, useState } from 'react';
import { CampaignRequestDetailResponse, INote, CampaignRequest, ApplicationSerializer } from '../../types/campaigns';

interface PossibleMatch {
  campaign_id: number;
  name: string;
  email: string;
  address: string;
  medical_reason: string;
  phone: string;
  status: string;
  match_type: 'email' | 'name' | 'address' | 'phone';
  ntaf_id: string;
  email_match_type?: 'primary_contact' | 'client';
  matched_email?: string;
}

interface RequestMatch {
  campaign_request_id: number;
  name: string;
  email: string;
  address: string;
  medical_reason: string;
  phone: string;
  status: string;
  match_type: 'email' | 'name' | 'address' | 'phone';
  created_date: string;
}

interface EmailMatchesSummary {
  has_email_matches: boolean;
  primary_contact_matches: PossibleMatch[];
  client_matches: PossibleMatch[];
}

export async function loader({ params }: { params: { id: string } }) {
  const [data, metadata] = await Promise.all([
    fetchCampaignRequest(params.id),
    fetchCampaignMetadata()
  ]);
  
  return {
    ...data,
    metadata
  };
}

const CampaignRequestDetail = () => {
  const loaderData = useLoaderData() as CampaignRequestDetailResponse & { metadata: unknown };
  const [campaignRequest, setCampaignRequest] = useState(loaderData.campaign_request);
  const [patient, setPatient] = useState(loaderData.patient);
  const [application, setApplication] = useState(loaderData.application);
  const [notes, setNotes] = useState<INote[]>([]);
  const [campaignRequestNotes, setCampaignRequestNotes] = useState<string>('');
  const [possibleMatches, setPossibleMatches] = useState<PossibleMatch[]>([]);
  const [requestMatches, setRequestMatches] = useState<RequestMatch[]>([]);
  const [emailMatchesSummary, setEmailMatchesSummary] = useState<EmailMatchesSummary | null>(null);
  const [activityLogRefreshKey, setActivityLogRefreshKey] = useState(0);
  const [possibleMatchesRefreshKey, setPossibleMatchesRefreshKey] = useState(0);

  // Function to refresh the activity log after any data update
  const refreshActivityLog = () => {
    setActivityLogRefreshKey((prev) => prev + 1);
  };

  const refreshPossibleMatches = () => {
    setPossibleMatchesRefreshKey((prev) => prev + 1);
  };
  
  const { 
    metadata, 
    campaign_notes: fetchedCampaignNotes, 
    possible_matches_count 
  } = loaderData;
  // const { id } = useParams();
  // const { data, isLoading, error } = useQuery({
  //   queryKey: ['campaignRequests', id],
  //   queryFn: () => fetchCampaignRequest(id),
  // });

  // if (isLoading) return <p>Loading...</p>;
  // if (error) return <p>Error: {error.message}</p>;

  // const { campaign, patient, campaign_request } = data;
  const pageTitle = 'Campaign Request';

  // Callback to update campaign request state
  const handleCampaignRequestUpdate = (updatedCampaignRequest: Partial<CampaignRequest>) => {
    setCampaignRequest(updatedCampaignRequest as CampaignRequest);
    refreshActivityLog();
    refreshPossibleMatches();
  };

  // Callback to handle patient updates from the API response
  const handlePatientUpdate = (apiResponse: CampaignRequestDetailResponse | Record<string, unknown>) => {
    // Handle both CampaignRequestDetailResponse and CampaignApplicationDetailSerializer formats
    if ('patient' in apiResponse && apiResponse.patient) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setPatient(apiResponse.patient as any);
    }
    
    // CampaignRequestDetailResponse has campaign_request directly
    // CampaignApplicationDetailSerializer has it nested in the response
    if ('campaign_request' in apiResponse && apiResponse.campaign_request) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setCampaignRequest(apiResponse.campaign_request as any);
    }
    
    // Handle application field
    // For CampaignApplicationDetailSerializer, reconstruct application from response
    if ('application' in apiResponse && apiResponse.application) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setApplication(apiResponse.application as any);
    } else if ('campaign_id' in apiResponse && apiResponse.campaign_id) {
      // Response is from CampaignApplicationDetailSerializer
      // Extract application fields by excluding nested objects
      const responseObj = apiResponse as Record<string, unknown>;
      const campaignId = responseObj.campaign_id as number;
      
      // Build application object by excluding nested fields
      const excludedKeys = ['campaign', 'patient', 'csc', 'campaign_request', 'notes', 'coordinator', 'approval', 'financial_coordinator', 'campaign_id'];
      const applicationFields: Record<string, unknown> = {};
      
      Object.keys(responseObj).forEach(key => {
        if (!excludedKeys.includes(key)) {
          applicationFields[key] = responseObj[key];
        }
      });
      
      setApplication({
        ...applicationFields,
        campaign: campaignId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    }
    
    if ('campaign_notes' in apiResponse && apiResponse.campaign_notes) {
      // Merge campaign notes with request notes
      const campaignRequestObj = 'campaign_request' in apiResponse ? apiResponse.campaign_request : undefined;
      const requestNotes = (campaignRequestObj as { request_notes?: unknown[] })?.request_notes || [];
      const allNotes = [...requestNotes, ...((apiResponse.campaign_notes as unknown[]) || [])];
      setNotes(allNotes as INote[]);
    }
    
    refreshActivityLog();
    refreshPossibleMatches();
  };

  // Callback to add a new note to the notes list
  const handleNoteAdd = (newNote: INote) => {
    setNotes((prevNotes) => [newNote, ...prevNotes]);
  };

  // Callback to update application state
  const handleApplicationUpdate = (updatedApplication: Partial<ApplicationSerializer>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setApplication(updatedApplication as any);
    refreshActivityLog();
  };

  useEffect(() => {
    // Merge campaign notes (Message objects) and campaign request notes (CampaignRequestNote objects)
    const requestNotes = campaignRequest?.request_notes || [];
    const allNotes = [
      ...requestNotes,
      ...(fetchedCampaignNotes || [])
    ];
    setNotes(allNotes);
    
    // Set the legacy notes string field from the campaign_request object
    if (campaignRequest?.notes) {
      setCampaignRequestNotes(campaignRequest.notes);
    }
  // Only run on initial load when fetchedCampaignNotes is available
  // Don't re-run when campaignRequest changes to avoid overwriting notes added via handleNoteAdd
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedCampaignNotes]);

  // Load possible matches on page load
  useEffect(() => {
    const loadPossibleMatches = async () => {
      if (campaignRequest?.id) {
        try {
          const response = await fetchCampaignRequestPossibleMatches(campaignRequest.id);
          // Handle both old and new API response formats
          if (response.matches) {
            setPossibleMatches(response.matches || []);
            setEmailMatchesSummary(response.email_matches_summary || null);
            setRequestMatches(response.request_matches || []);
          } else {
            // Legacy format
            setPossibleMatches(response || []);
            setEmailMatchesSummary(null);
            setRequestMatches([]);
          }
        } catch (error) {
          console.error('Error loading possible matches:', error);
          setPossibleMatches([]);
          setRequestMatches([]);
          setEmailMatchesSummary(null);
        }
      }
    };

    loadPossibleMatches();
  }, [campaignRequest?.id, possibleMatchesRefreshKey]);

  return (
    <>
      <Breadcrumb
        pageName={pageTitle}
        parentPage='Campaign Requests'
        parentPath='/campaign-requests'
      />

      <NameHeader 
        campaignRequest={campaignRequest} 
        patient={patient} 
        metadata={metadata}
        application={application}
        emailMatchesSummary={emailMatchesSummary}
        onCampaignRequestUpdate={handleCampaignRequestUpdate}
        onApplicationUpdate={handleApplicationUpdate}
        onNoteAdd={handleNoteAdd}
      />

      <div className='grid grid-cols-12 gap-7.5'>
        <div className='col-span-12 xl:col-span-8'>
          <InformationTabs
            campaignRequest={campaignRequest}
            patient={patient}
            metadata={metadata}
            application={application}
            possibleMatchesCount={possible_matches_count || 0}
            possibleMatches={possibleMatches}
            requestMatches={requestMatches}
            onPatientUpdate={handlePatientUpdate}
          />
        </div>
        <div className='col-span-12 xl:col-span-4'>
          <AssignedTo
            csc={campaignRequest?.patient_services_coordinator?.person}
            campaignRequestId={campaignRequest?.id}
            campaignRequest={campaignRequest as CampaignRequest & { id: number }}
            onCampaignRequestUpdate={handleCampaignRequestUpdate}
            metadata={metadata}
          />
          <div className='mt-7.5'>
            <ActivitiesTabs
              notes={notes}
              setNotes={setNotes}
              campaign={campaignRequest?.campaign}
              campaignRequest={campaignRequest}
              campaignRequestNotes={campaignRequestNotes}
              onRequestNotesUpdate={(updatedNotes) => setCampaignRequestNotes(updatedNotes)}
              activityLogRefreshKey={activityLogRefreshKey}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignRequestDetail;
